import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { equals, reject } from 'ramda'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { DropdownMultiselectContext } from './context'
import { Selection } from './Selection'

interface DropdownProps extends React.HTMLProps<HTMLDivElement> {
  placeholder?: string
  value: string[]
  open?: boolean
  asDialog?: boolean
  onOpenChange?: (open: boolean) => void
  onValueChange?: (newValue: string[]) => void
  renderItemsAs?: (optionProps: { label: string, isSelected: boolean }) => React.ReactNode
}

const Wrapper = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) => $disabled && modDisabled()}
`

export function Root({
  className,
  children,
  placeholder,
  asDialog = false,
  disabled,
  value,
  open,
  onOpenChange,
  onValueChange,
}: DropdownProps) {
  const handleOptionAdd = React.useCallback(
    (addedValue: string) => {
      onValueChange?.([...value, addedValue])
    },
    [onValueChange, value],
  )

  const handleOptionRemove = React.useCallback(
    (removedValue: string) => {
      onValueChange?.(reject(equals(removedValue), value))
    },
    [onValueChange, value],
  )

  const handleOptionsAdd = React.useCallback(
    (addedValues: string[]) => {
      onValueChange?.([...value, ...addedValues])
    },
    [onValueChange, value],
  )

  const handleOptionsRemove = React.useCallback(
    (removedValues: string[]) => {
      onValueChange?.(value.filter(val => !removedValues.includes(val)))
    },
    [onValueChange, value],
  )

  return (
    <Wrapper className={className} $disabled={disabled}>
      <DropdownMultiselectContext.Provider
        value={{
          placeholder,
          disabled,
          selectedValues: value,
          asDialog,
          removeOption: handleOptionRemove,
          addOption: handleOptionAdd,
          removeOptions: handleOptionsRemove,
          addOptions: handleOptionsAdd,
        }}
      >
        {asDialog
          ? (
              <Dialog.Root open={disabled ? false : open} onOpenChange={onOpenChange}>
                {children}
              </Dialog.Root>
            )
          : (
              <Popover.Root open={disabled ? false : open} onOpenChange={onOpenChange}>
                {children}
              </Popover.Root>
            )}
      </DropdownMultiselectContext.Provider>
    </Wrapper>
  )
}

function modDisabled() {
  return css`
  ${Selection} {
    ${Selection.modDisabled()}
  }
`
}
